import React, { PropsWithChildren } from 'react';
import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { Box, CircularProgress } from '@mui/material';
import { AWAIT_BUY_IN_PROGRESS_BAR_THICKNESS } from 'constants/gameConstants';
import { useTableSelector } from 'hooks/useTableSelector';
import { getPercentage } from 'utils/functions';

import { getProgressColor } from './';

type TimerType = PropsWithChildren & {
  isStartProgress: boolean;
  size: number;
};

type CountDownTimerPropType = TimerType & {
  size: number;
  onComplete?: () => void;
};

const CountDownTimer = ({
  isStartProgress,
  size,
  onComplete = () => {}
}: CountDownTimerPropType) => {
  const { sBustPause, bust } = useTableSelector((table) => table.game);
  const [progress, setProgress] = useState(0);
  const [countDown, setCountDown] = useState(+sBustPause);
  const proxyRef = useRef({ progress: 0 });
  const tweenRef = useRef<gsap.core.Tween | null>(null);

  useEffect(() => {
    if (tweenRef.current) tweenRef.current.kill();

    if (isStartProgress && bust) {
      proxyRef.current.progress = bust?.elapsedTime ?? 0;
      // Only setState when the integer countdown actually ticks down (~1Hz).
      // The CircularProgress has a 1s CSS transition, so it smooths between
      // value changes — we don't need 60Hz React renders to look smooth.
      let lastCountdown = Number.NaN;

      tweenRef.current = gsap.to(proxyRef.current, {
        progress: +sBustPause,
        duration: (bust?.duration ?? 0) / 1000,
        ease: 'none',
        onUpdate: () => {
          const val = proxyRef.current.progress;
          const countDownValue = +sBustPause - Math.floor(val);
          if (countDownValue === lastCountdown) return;
          lastCountdown = countDownValue;
          setProgress(getPercentage(val, +sBustPause));
          setCountDown(countDownValue);
          if (countDownValue <= 0) {
            onComplete();
          }
        }
      });
    }

    return () => {
      if (tweenRef.current) tweenRef.current.kill();
    };
  }, [isStartProgress, bust, sBustPause]);

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex', height: size, width: size }}>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          padding: `${AWAIT_BUY_IN_PROGRESS_BAR_THICKNESS * 2.25}px`,
          fontSize: '3.5rem',
          borderRadius: '100%',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center'
        }}>
        {countDown}
      </Box>
      {isStartProgress && (
        <CircularProgress
          variant='determinate'
          value={Math.max(100 - progress, 0)}
          thickness={AWAIT_BUY_IN_PROGRESS_BAR_THICKNESS}
          size={size}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            color: getProgressColor(progress),
            transition: 'all 1s ease'
          }}
        />
      )}
    </Box>
  );
};

export default CountDownTimer;
